// What kind of token the token panel is showing, and the words its figures
// use for that kind. A fund token has no exchange volume and its mints are
// subscriptions with the issuer, so the stock wording ("not reported by the
// venue") would mislead there. Pure, so every kind's copy is tested.

export type TokenKind = "stock" | "fund" | "commodity" | "stablecoin" | "protocol";

/**
 * Stock venues publish tokens under their own id ("robinhood", "ondo"…);
 * registry class tokens sit under "issuer:<id>". The class page the panel
 * opened from tells a commodity or stablecoin from a fund.
 */
export function tokenKind({ venueId, tokenId, classId }: { venueId: string; tokenId: string; classId?: string | null }): TokenKind {
  if (tokenId.startsWith("offchain:")) return "protocol";
  if (!venueId.startsWith("issuer:")) return "stock";
  if (classId === "commodities" || classId === "commodity") return "commodity";
  if (classId === "stablecoins" || classId === "stablecoin") return "stablecoin";
  return "fund";
}

export interface FigureCopy {
  /** Under the price when it is a NAV or reference price rather than a trade. */
  priceWhat: string | null;
  /** The 24h change's note once it exists. */
  change: string;
  volumeLabel: string;
  volumeMissing: string;
  volumePresent: string;
  mintBurnLabel: string;
  mintBurnMissing: string;
  /** Present note; "today" or "last 24h" is added by the panel. */
  mintBurnPresent: string;
}

const STOCK: FigureCopy = {
  priceWhat: null,
  change: "token price vs 24h ago",
  volumeLabel: "Trading volume",
  volumeMissing: "not reported by the venue",
  volumePresent: "venue-reported, today, USD",
  mintBurnLabel: "Mint + burn",
  mintBurnMissing: "flow not measured yet",
  mintBurnPresent: "USD minted + burned",
};

export const FIGURE_COPY: Record<TokenKind, FigureCopy> = {
  stock: STOCK,
  fund: {
    priceWhat: "issuer NAV",
    change: "price vs 24h ago",
    volumeLabel: "Trading volume",
    volumeMissing: "none reported: subscriptions and redemptions happen with the issuer",
    volumePresent: "reported trading, today, USD",
    mintBurnLabel: "Subscriptions + redemptions",
    mintBurnMissing: "issued and redeemed with the issuer; not measured on chain yet",
    mintBurnPresent: "USD issued + redeemed on chain",
  },
  commodity: {
    priceWhat: "reference metal price",
    change: "price vs 24h ago",
    volumeLabel: "Trading volume",
    volumeMissing: "none reported: we read issuance on chain, not exchange trading",
    volumePresent: "reported trading, today, USD",
    mintBurnLabel: "Mint + redeem",
    mintBurnMissing: "minted and redeemed against the issuer's vaulted metal; not measured yet",
    mintBurnPresent: "USD minted + redeemed",
  },
  stablecoin: {
    priceWhat: "market price; the peg is the target",
    change: "price vs 24h ago",
    volumeLabel: "Trading volume",
    volumeMissing: "not reported",
    volumePresent: "reported trading, today, USD",
    mintBurnLabel: "Issued + redeemed",
    mintBurnMissing: "issuance and redemption not measured yet",
    mintBurnPresent: "USD issued + redeemed",
  },
  protocol: {
    priceWhat: "no single token: valued by protocol TVL",
    change: "TVL vs 24h ago",
    volumeLabel: "Trading volume",
    volumeMissing: "not applicable to a protocol",
    volumePresent: "reported, today, USD",
    mintBurnLabel: "Deposits + withdrawals",
    mintBurnMissing: "not measured for a protocol",
    mintBurnPresent: "USD deposited + withdrawn",
  },
};

/** Price bases that are a NAV or reference figure, not a trade. */
const REFERENCE_BASES = new Set(["nav_api", "chainlink", "onchain_oracle", "erc4626", "fixed"]);

/** The note under the price: what kind of price it is (when known) and how old. */
export function priceNote(kind: TokenKind, basis: string | null | undefined, age: string | null): string | undefined {
  const copy = FIGURE_COPY[kind];
  const what = copy.priceWhat && (kind === "protocol" || kind === "stablecoin" || (basis && REFERENCE_BASES.has(basis))) ? copy.priceWhat : null;
  const parts = [what, age].filter((p): p is string => Boolean(p));
  return parts.length ? parts.join(", ") : undefined;
}
